"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.debugGroupWalk = exports.getRecordGroupId = exports.getSizeByGroupWalkEvent = exports.groupWalkSortedRecords = exports.groupLevels = void 0;
exports.groupLevels = ["top", "middle", "last", "single"];
/**
 *
 * Generator to walk through plain list of items as if they are grouped.
 * Yields events like 'group-start', 'record', 'record', 'group-end'.
 *
 * Receives:
 *
 * @param records - actual list of items to render
 * @param keys - key functions to group by
 * @param collapsedGroups - defines which groups are collapsed and so must have content hidden.
 * @param callback
 * For hidden content, each event contains a `hidden` flag.
 * from the middle of the list. This allows us to detect if we are already inside
 * the hidden group and if we must open/close groups as we go along.
 *
 */
function groupWalkSortedRecords(records, keys, collapsedGroups, callback) {
    const currentGroup = [];
    let currentCollapsedGroup = null;
    let parentGroupId;
    let recordIndex = 0;
    let globalIndex = 0;
    const groupRecords = {};
    for (const record of records) {
        let recordGroup = [];
        recordGroup = [];
        // check if we should open or close a group
        for (let level = 0; level < keys.length; level++) {
            const key = keys[level];
            const groupKeyData = key(record);
            recordGroup.push(groupKeyData);
            const recordGroupId = getGroupId(recordGroup);
            if (!groupRecords[recordGroupId]) {
                groupRecords[recordGroupId] = [];
            }
            groupRecords[recordGroupId].push(record);
            // We check whether we are at the same group level. If level isn't same,
            // then we need to close group and open other.
            // We don’t need to do anything if the records belong to one group
            if (isSameGroupLevel(currentGroup, recordGroup)) {
                // record is the same as the last one - put margin between them
                callback({
                    event: "margin-record",
                    level: getGroupLevel(currentGroup.length, keys),
                    hidden: !!currentCollapsedGroup,
                });
            }
            else {
                // check if we should close a group
                let ended = false;
                let groupLevel;
                while (currentGroup.length >= recordGroup.length &&
                    !doesIncludeGroup(recordGroup, currentGroup)) {
                    let hidden = false;
                    if (currentCollapsedGroup) {
                        if (groupIsEqual(currentCollapsedGroup, currentGroup)) {
                            currentCollapsedGroup = null;
                        }
                        hidden = true;
                    }
                    const currentGroupId = getGroupId(currentGroup);
                    groupLevel = getGroupLevel(currentGroup.length, keys);
                    // end of group
                    callback({
                        event: "end-group",
                        groupId: currentGroupId,
                        level: groupLevel,
                        depth: currentGroup.length - 1,
                        hidden,
                        data: groupKeyData.data,
                        records: groupRecords[currentGroupId],
                    });
                    ended = true;
                    currentGroup.pop();
                }
                if (ended) {
                    // we have closed something before - it means that new gropu is not the first one,
                    // so we need to add a margin before starting a new group
                    callback({
                        event: "margin-group",
                        // @ts-expect-error original functionality
                        level: groupLevel,
                        hidden: !!currentCollapsedGroup,
                    });
                }
                // open new group
                if (currentGroup.length <= level) {
                    recordIndex = 0;
                    currentGroup[level] = groupKeyData;
                    let hidden = !!currentCollapsedGroup;
                    if (!currentCollapsedGroup && groupIsInCollapsed(collapsedGroups, currentGroup)) {
                        currentCollapsedGroup = [...currentGroup];
                        hidden = false;
                    }
                    callback({
                        event: "start-group",
                        parentGroupId,
                        groupId: recordGroupId,
                        level: getGroupLevel(currentGroup.length, keys),
                        depth: currentGroup.length - 1,
                        hidden,
                        data: groupKeyData.data,
                        records: [],
                        groupKey: key,
                    });
                }
            }
            parentGroupId = recordGroupId;
        }
        callback({
            event: "record",
            record,
            index: recordIndex,
            globalIndex,
            level: getGroupLevel(recordGroup.length, keys),
            groupId: getGroupId(recordGroup),
            hidden: !!currentCollapsedGroup,
        });
        recordIndex += 1;
        globalIndex += 1;
        parentGroupId = undefined;
    }
    // we should close all groups
    while (currentGroup.length > 0) {
        let hidden = false;
        if (currentCollapsedGroup) {
            if (groupIsEqual(currentCollapsedGroup, currentGroup)) {
                currentCollapsedGroup = null;
            }
            hidden = true;
        }
        const groupKey = currentGroup[currentGroup.length - 1];
        const currentGroupId = getGroupId(currentGroup);
        callback({
            event: "end-group",
            groupId: currentGroupId,
            level: getGroupLevel(currentGroup.length, keys),
            hidden,
            data: groupKey.data,
            records: groupRecords[currentGroupId],
        });
        currentGroup.pop();
    }
}
exports.groupWalkSortedRecords = groupWalkSortedRecords;
function getSizeByGroupWalkEvent(sizeMap, step) {
    if (step.hidden) {
        return 0;
    }
    let level;
    switch (step.event) {
        case "start-group":
        case "margin-group":
            level = step.level === "single" ? "top" : step.level;
            break;
        case "end-group":
            level = step.level === "single" ? "last" : step.level;
            break;
    }
    // @ts-expect-error original functionality
    return level && sizeMap[`${step.event}-${level}`] !== undefined
        ? // @ts-expect-error original functionality
            sizeMap[`${step.event}-${level}`]
        : // @ts-expect-error original functionality
            sizeMap[step.event];
}
exports.getSizeByGroupWalkEvent = getSizeByGroupWalkEvent;
const doesIncludeGroup = (one, two) => {
    for (let index = 0; index < one.length; index++) {
        if (one[index].key !== two[index].key) {
            return false;
        }
    }
    return true;
};
const groupIsEqual = (one, two) => {
    if (one.length < two.length) {
        return false;
    }
    for (let index = 0; index < one.length; index++) {
        if (one[index].key !== two[index].key) {
            return false;
        }
    }
    return true;
};
const isSameGroupLevel = (wrapper, contained) => {
    return wrapper.length === contained.length && doesIncludeGroup(wrapper, contained);
};
const groupIsInCollapsed = (collapsedGroupIds, currentGroup) => {
    return collapsedGroupIds.has(getGroupId(currentGroup));
};
const getGroupId = (currentGroup) => {
    return currentGroup.map(({ key }) => key || "Empty").join("|");
};
const getRecordGroupId = (record, keys) => {
    const recordGroup = [];
    for (const key of keys) {
        const groupKeyData = key(record);
        recordGroup.push(groupKeyData);
    }
    return getGroupId(recordGroup);
};
exports.getRecordGroupId = getRecordGroupId;
const getGroupLevel = (currentGroupLevel, keys) => {
    if (keys.length === 1) {
        return "single";
    }
    else {
        return currentGroupLevel === keys.length ? "last" : currentGroupLevel === 1 ? "top" : "middle";
    }
};
const debugGroupWalk = (step, sizeMap, recordKey, getRecordHeight) => {
    /* eslint-disable no-console */
    switch (step.event) {
        case "start-group":
            console.log("-----  START", step === null || step === void 0 ? void 0 : step.groupId, "----", "l: ", step === null || step === void 0 ? void 0 : step.level, ", s:", getSizeByGroupWalkEvent(sizeMap, step));
            break;
        case "end-group":
            console.log("-----  END", step === null || step === void 0 ? void 0 : step.groupId, "----", "l: ", step === null || step === void 0 ? void 0 : step.level, ", s:", getSizeByGroupWalkEvent(sizeMap, step));
            break;
        case "record":
            console.log("record", recordKey(step.record), " s:", getRecordHeight(step.record), "i: ", step.index);
            break;
        case "margin-group":
            console.log(step.event, "l: ", step === null || step === void 0 ? void 0 : step.level, ", s:", getSizeByGroupWalkEvent(sizeMap, step));
            break;
        default:
            console.log(step.event, ", s:", getSizeByGroupWalkEvent(sizeMap, step));
    }
    /* eslint-enable no-console */
};
exports.debugGroupWalk = debugGroupWalk;
